import { type ConversaDetalhe, type MensagemChat } from '@mymaxx/shared';
import {
  arquivoDeAudio,
  Aviso,
  BarraGravacao,
  Botao,
  BotaoMicrofone,
  Citacao,
  ImagemColada,
  imagemDaColagem,
  useRolagemDoFim,
  useGravadorAudio,
} from '@mymaxx/ui';
import { Paperclip, Send, StickyNote, Zap } from 'lucide-react';
import { useRef, useState, type ClipboardEvent, type FormEvent } from 'react';
import { ErroApi } from '../lib/api';
import { atendimentoApi } from '../lib/atendimento-api';
import { Bolha } from './Bolha';
import { BarraDoCorretor, corretorLigado, useRevisao } from './Corretor';

/** Chat do atendente: histórico, envio, nota interna, anexo e respostas rápidas. */
export function Conversa({
  conversa,
  mensagens,
  respostasRapidas,
  onMudou,
}: {
  conversa: ConversaDetalhe;
  mensagens: MensagemChat[];
  respostasRapidas: { id: string; titulo: string; texto: string }[];
  onMudou: () => void;
}) {
  const [texto, setTexto] = useState('');
  const [modoNota, setModoNota] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [respondendo, setRespondendo] = useState<MensagemChat | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [mostrarRapidas, setMostrarRapidas] = useState(false);
  /** Imagem colada esperando confirmação (ver `colar`). */
  const [colado, setColado] = useState<File | null>(null);
  const fim = useRef<HTMLDivElement>(null);
  const arquivo = useRef<HTMLInputElement>(null);

  useRolagemDoFim(fim, `${conversa.id}:${mensagens.length}`);

  const encerrada = conversa.status === 'encerrada';
  const minha = conversa.status !== 'fila';

  // O corretor olha o que vai ao cliente e a nota interna; áudio não tem texto a corrigir.
  const [corretor, setCorretor] = useState(corretorLigado);
  const revisao = useRevisao(texto, corretor && !!minha);

  const gravador = useGravadorAudio({
    onErro: setErro,
    onGravado: (audio, mimeType) => void anexar(arquivoDeAudio(audio, mimeType)),
  });

  async function enviar(evento?: FormEvent) {
    evento?.preventDefault();
    // Gravando, o botão de enviar é o mesmo — ele fecha o áudio, e o envio segue pelo
    // caminho do anexo quando o gravador devolver o arquivo.
    if (gravador.gravando) {
      gravador.finalizar();
      return;
    }
    const conteudo = texto.trim();
    if (!conteudo || enviando) return;
    setEnviando(true);
    setErro(null);
    try {
      if (modoNota) await atendimentoApi.anotar(conversa.id, conteudo);
      else await atendimentoApi.responder(conversa.id, conteudo, respondendo?.id);
      setTexto('');
      setRespondendo(null);
      onMudou();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Não consegui enviar');
    } finally {
      setEnviando(false);
    }
  }

  /**
   * Imagem colada com Ctrl+V (o print que o Windows guarda na área de transferência).
   *
   * Antes o atendente tinha de salvar o print em arquivo, achar a pasta e anexar — três
   * passos para mandar o que já estava na memória do computador. A Tecla Windows+Shift+S
   * recorta e copia; aqui ele cola.
   *
   * O envio NÃO é automático: um Ctrl+V distraído mandaria para o cliente o que estivesse
   * copiado. A imagem aparece em miniatura e vai embora quando ele confirma.
   */
  function colar(evento: ClipboardEvent) {
    if (!minha || encerrada) return;
    const imagem = imagemDaColagem(evento.clipboardData);
    if (!imagem) return;
    evento.preventDefault();
    setColado(imagem);
  }

  async function anexar(file: File) {
    setEnviando(true);
    setErro(null);
    try {
      await atendimentoApi.enviarArquivo(conversa.id, file);
      onMudou();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Não consegui enviar o arquivo');
    } finally {
      setEnviando(false);
      if (arquivo.current) arquivo.current.value = '';
    }
  }

  return (
    // `min-h-0` nos dois níveis, e não é detalhe: sem ele um item de coluna flex tem
    // altura mínima automática, cresce até caber todo o conteúdo e o `overflow-y-auto`
    // abaixo nunca chega a ter o que rolar. O efeito era o fio de mensagens estourar a
    // caixa, o pai cortar o começo da conversa e não existir barra para subir — quem
    // abria um atendimento encerrado não conseguia ler o que tinha acontecido nele.
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto bg-neutral-50 p-4 dark:bg-neutral-950">
        {/* Largura máxima da leitura: em tela ultrawide a coluna do chat passa de 1200px e
            a bolha esticava quase um metro de texto. O fio fica centralizado, como em
            qualquer mensageiro. */}
        <div className="mx-auto w-full max-w-3xl space-y-2">
          {mensagens.map((m) => (
            <Bolha key={m.id} mensagem={m} onResponder={() => setRespondendo(m)} />
          ))}
          <div ref={fim} />
        </div>
      </div>

      {erro && (
        <div className="px-4 pt-2">
          <Aviso tipo="erro">{erro}</Aviso>
        </div>
      )}

      {encerrada ? (
        <p className="border-t border-neutral-200 p-4 text-center text-sm text-neutral-500 dark:border-neutral-800">
          Atendimento encerrado
          {conversa.avaliacao ? ` · cliente avaliou com ${conversa.avaliacao} de 5` : ''}
          {conversa.avaliacaoComentario ? ` — “${conversa.avaliacaoComentario}”` : ''}
        </p>
      ) : (
        <form
          onSubmit={enviar}
          className="space-y-2 border-t border-neutral-200 p-3 dark:border-neutral-800"
        >
          {mostrarRapidas && respostasRapidas.length > 0 && (
            <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-neutral-200 p-2 dark:border-neutral-700">
              {respostasRapidas.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => {
                    setTexto(r.texto);
                    setMostrarRapidas(false);
                  }}
                  className="block w-full rounded px-2 py-1 text-left text-xs hover:bg-neutral-100 dark:hover:bg-neutral-800"
                >
                  <span className="font-medium">{r.titulo}</span>
                  <span className="ml-2 text-neutral-500">{r.texto.slice(0, 60)}…</span>
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 text-xs">
            <button
              type="button"
              onClick={() => setModoNota((v) => !v)}
              className={`flex items-center gap-1 rounded px-2 py-1 ${
                modoNota
                  ? 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200'
                  : 'text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800'
              }`}
            >
              <StickyNote size={13} /> Nota interna
            </button>
            <button
              type="button"
              onClick={() => setMostrarRapidas((v) => !v)}
              className="flex items-center gap-1 rounded px-2 py-1 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
            >
              <Zap size={13} /> Respostas rápidas
            </button>
            {!minha && <span className="text-amber-600">Assuma o atendimento para responder</span>}
          </div>

          {colado && (
            <ImagemColada
              arquivo={colado}
              enviando={enviando}
              onCancelar={() => setColado(null)}
              onEnviar={() => {
                const arquivo = colado;
                setColado(null);
                void anexar(arquivo);
              }}
            />
          )}

          {respondendo && (
            <Citacao
              autor={
                respondendo.de === 'cliente'
                  ? (conversa.cliente ?? 'Cliente')
                  : (respondendo.autorNome ?? 'Atendimento')
              }
              resumo={respondendo.texto?.trim() || 'Anexo'}
              onCancelar={() => setRespondendo(null)}
              className="bg-black/5 dark:bg-white/10"
            />
          )}

          {/* As correções propostas, acima da linha de botões: são para serem lidas antes
              de enviar, e embaixo ficariam atrás do teclado em tela pequena. */}
          {!gravador.gravando && (
            <BarraDoCorretor
              texto={texto}
              achados={revisao.achados}
              onTexto={setTexto}
              ligado={corretor}
              onLigado={setCorretor}
            />
          )}

          <div className="flex items-end gap-2">
            <input
              ref={arquivo}
              type="file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void anexar(f);
              }}
            />
            {/* Gravando, a onda toma o lugar do campo e dos anexos — só a lixeira, o
                relógio e a pausa ficam ali, e quem envia continua sendo o mesmo botão. */}
            {gravador.gravando ? (
              <BarraGravacao gravador={gravador} />
            ) : (
              <>
                <button
                  type="button"
                  aria-label="Anexar arquivo"
                  disabled={!minha}
                  onClick={() => arquivo.current?.click()}
                  className="rounded-lg p-2 text-neutral-500 hover:bg-neutral-100 disabled:opacity-40 dark:hover:bg-neutral-800"
                >
                  <Paperclip size={18} />
                </button>
                <BotaoMicrofone gravador={gravador} desabilitado={!minha || enviando} />
                <textarea
                  rows={2}
                  value={texto}
                  disabled={!minha}
                  aria-label={modoNota ? 'Nota interna' : 'Resposta ao cliente'}
                  placeholder={
                    modoNota ? 'Anotação que só a equipe vê…' : 'Escreva a resposta ao cliente…'
                  }
                  onChange={(e) => setTexto(e.target.value)}
                  onPaste={colar}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void enviar();
                    }
                  }}
                  className={`max-h-40 flex-1 resize-none rounded-lg border px-3 py-2 text-sm disabled:opacity-60 dark:bg-neutral-900 ${
                    modoNota
                      ? 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40'
                      : 'border-neutral-300 dark:border-neutral-700'
                  }`}
                />
              </>
            )}
            <Botao
              type="submit"
              carregando={enviando}
              disabled={!minha}
              aria-label={gravador.gravando ? 'Enviar áudio' : 'Enviar'}
            >
              <Send size={16} />
            </Botao>
          </div>
        </form>
      )}
    </div>
  );
}
